import { Component, inject, signal } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { filter, Observable } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import {
    EnedisAccessRightModel,
    EnedisPowerBusinessMetricLabels,
    EnedisPowerData,
    EnedisPowerDataPoint,
    EnedisPowerMaxStep,
    EnedisPowerMaxStepLabel,
    EnedisPowerPhysicalMetric,
    EnedisPowerMeanStep,
    EnedisPowerMeanStepLabel,
    EnedisPowerMeasuresTypeLabel,
    EnedisPowerMeasuresType,
} from '../../../enedis.models';
import { EnedisState } from '../../../enedis.state';
import { EnedisService } from '../../../enedis.service';
import {
    DisplayedColumns,
    DisplayType,
} from 'src/app/shared/components/ed-table/ed-table.component';
import { AlertService, ButtonColors } from '@ed---interne/ng-uui-components';
import { ApexAxisChartSeries, ApexDataLabels, ApexYAxis } from 'ng-apexcharts';
import { convertNumber } from 'src/app/shared/all.helpers';

interface PowerTableElement extends Partial<EnedisPowerDataPoint> {}

@UntilDestroy()
@Component({
    selector: 'app-enedis-meter-power-curve',
    templateUrl: './enedis-meter-power-curve.component.html',
    styleUrls: ['./enedis-meter-power-curve.component.scss'],
})
export class EnedisMeterPowerCurveComponent {
    public enedisAccessRight$: Observable<EnedisAccessRightModel> = inject(Store)
        .select(EnedisState.currentAccessRight)
        .pipe(filter(Boolean));
    public accessRight: EnedisAccessRightModel | null = null;

    public forceReload$: Observable<boolean> = inject(Store)
        .select(EnedisState.forceReload)
        .pipe(filter(Boolean));
    public forceReload: boolean = false;

    private now = new Date();

    dateStart: Date = new Date(this.now.getFullYear() - 1, this.now.getMonth(), this.now.getDate());
    dateEnd: Date = new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate());

    step: string = EnedisPowerMaxStep.MONTHLY;
    stepOptions = Object.entries(EnedisPowerMaxStepLabel).map(([value, label]) => ({
        value,
        label,
    }));

    typeOptions = Object.entries(EnedisPowerMeasuresTypeLabel).map(([value, label]) => ({
        value,
        label,
    }));
    typePowerMax = EnedisPowerMeasuresType.PMAX;
    type = this.typePowerMax;

    powerData: ApexAxisChartSeries = [];
    loading = signal(false);
    allowReload = signal(false);

    powerChartDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    powerChartYaxis: ApexYAxis = {
        title: { text: 'Puissance' },
        labels: {
            formatter: (value: number) => convertNumber(value),
        },
    };
    buttonColorsSecondary = ButtonColors.Secondary;
    buttonColorsPrimary = ButtonColors.Primary;

    public powerDataSource: MatTableDataSource<PowerTableElement> =
        new MatTableDataSource<PowerTableElement>();
    public powerDisplayedColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.DATETIME,
            objectDisplayName: 'Début',
            objectKey: 'dateStart',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Puissance',
            objectKey: 'powerValue',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Step',
            objectKey: 'step',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nature',
            objectKey: 'nature',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Indice de confiance',
            objectKey: 'indicatorConfidence',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'complementaryState',
            objectKey: 'complementaryState',
            bold: false,
        },
    ];

    constructor(
        private readonly enedisService: EnedisService,
        private readonly alertService: AlertService,
    ) {
        this.enedisAccessRight$.pipe(untilDestroyed(this)).subscribe((enedisAccessRight) => {
            this.accessRight = enedisAccessRight;
            this.fetchData();
        });
        this.forceReload$.pipe(untilDestroyed(this)).subscribe((forceReload: boolean) => {
            this.forceReload = forceReload;
            if (forceReload) {
                this.fetchData();
            }
        });
    }

    async updateDateStart(dateStart: Date) {
        this.dateStart = dateStart;
        this.allowReload.set(true);
    }
    async updateDateEnd(dateEnd: Date) {
        this.dateEnd = new Date(Math.min(dateEnd.getTime(), new Date().getTime()));
        this.allowReload.set(true);
    }

    async updateType(type: EnedisPowerMeasuresType) {
        //Change date if we change type
        //From MEAN TO MAX : change to One year
        if (this.type == EnedisPowerMeasuresType.PMEAN && type == EnedisPowerMeasuresType.PMAX) {
            this.dateStart = new Date(
                this.dateEnd.getFullYear() - 1,
                this.dateEnd.getMonth(),
                this.dateEnd.getDate(),
            );
        }
        //From Max TO MEAN : change to One day
        if (this.type == EnedisPowerMeasuresType.PMAX && type == EnedisPowerMeasuresType.PMEAN) {
            this.dateStart = new Date(
                this.dateEnd.getFullYear(),
                this.dateEnd.getMonth(),
                this.dateEnd.getDate() - 1,
            );
        }
        this.type = type;
        this.allowReload.set(true);
    }

    async updateStep(step: EnedisPowerMaxStep) {
        if (step === EnedisPowerMaxStep.DAILY) {
            const oneMonthRecommended = new Date(this.dateEnd);
            oneMonthRecommended.setMonth(oneMonthRecommended.getMonth() - 1);
            this.dateStart = new Date(
                Math.max(this.dateStart.getTime(), oneMonthRecommended.getTime()),
            );
        }
        this.step = step;
        this.allowReload.set(true);
    }

    async fetchData(): Promise<void> {
        if (!this.dateStart || !this.dateEnd || !this.accessRight) return;
        if (this.dateStart.getTime() >= this.dateEnd.getTime()) {
            this.alertService.warning(
                'Dates invalides',
                `La date de début doit être inférieure à la date de fin`,
            );
            return;
        }

        // Vérifie si dateStart est plus vieux que 24 mois
        const limitDate = new Date();
        limitDate.setMonth(limitDate.getMonth() - 24);
        if (this.dateStart < limitDate) {
            this.alertService.warning(
                'Dates invalides',
                `Vous ne pouvez récupérer l'historique au dela de 2 ans`,
            );
            return;
        }

        // Puissacen moyennes : MEAN :  Vérifie si l'écart entre les deux dates est supérieur à 14 jours
        const diffTime = this.dateEnd.getTime() - this.dateStart.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        if (this.type === EnedisPowerMeasuresType.PMEAN && diffDays > 14) {
            this.alertService.warning(
                'Dates invalides',
                "L'intervalle entre les deux dates ne peut dépasser 14 jours pour les données infra quotidiennes",
            );
            return;
        } else if (
            this.type === EnedisPowerMeasuresType.PMAX &&
            this.step === EnedisPowerMaxStep.DAILY &&
            diffDays > 60
        ) {
            this.alertService.warning(
                'Dates invalides',
                "L'intervalle entre les deux dates ne peut dépasser 60 jours pour les données quotidiennes.",
            );
            return;
        }

        this.loading.set(true);
        this.powerData = [];
        if (this.accessRight) {
            try {
                let data: EnedisPowerData[] = await this.enedisService.getPowerCurve(
                    this.accessRight.id,
                    this.dateStart,
                    this.dateEnd,
                    this.type,
                    this.step,
                    this.forceReload,
                );

                if (data.length == 0) {
                    this.alertService.warning('Profil', 'Aucune donnée sur cette periode');
                    this.loading.set(false);
                    return;
                }

                this.powerChartYaxis.title!.text = EnedisPowerMeasuresTypeLabel[this.type];

                this.powerData = data.map((serie: EnedisPowerData) => ({
                    name: `${EnedisPowerBusinessMetricLabels[serie.businessMetric]} (${serie.powerUnit})  ${EnedisPowerMeanStepLabel[serie.points[0].step] ?? serie.physicalMetric ?? ' '} `,
                    type: 'bar',
                    data: serie.points.map((d: EnedisPowerDataPoint) => ({
                        x: d.dateStart,
                        y: d.powerValue,
                    })),
                }));
                this.powerChartDataLabels.enabledOnSeries = this.powerData.map((_, i) => i);

                this.fillTable(data);

                this.allowReload.set(false);
                this.loading.set(false);
            } catch (error: any) {
                console.log(error);
                this.powerData = [];
                this.loading.set(false);
                this.alertService.error(
                    'Erreur lors de la récupération des profils de puissance',
                    `${error?.error?.message ?? 'Une erreur est survenue'}`,
                );
            }
        }
    }

    fillTable(data: EnedisPowerData[]) {
        this.powerDataSource = new MatTableDataSource<PowerTableElement>(data[0].points);
    }
}
